/**
 * Cast-mode bottom progress strip for Lumyn Live host.
 *
 * Renders only while projector mode is active. Shows Q N of M + a circular
 * count-up ring against a 30-second budget. Once elapsed exceeds the budget
 * the ring pulses amber-then-rose to signal "linger time".
 *
 * Extracted verbatim from LiveHostPage.jsx during the Phase 2 refactor
 * (no behaviour change).
 */
import { useState, useEffect } from 'react';

const QUESTION_BUDGET_SECS = 30; // "soft" per-question countdown for the projection ring

export function CastProgressBar({ session }) {
  const [elapsed, setElapsed] = useState(0);
  const totalQs = session.total_questions || 1;
  const idx = session.current_question_index;
  const startedAt = session.question_started_at;

  useEffect(() => {
    if (!startedAt) { setElapsed(0); return undefined; }
    const startMs = new Date(startedAt).getTime();
    const tick = () => setElapsed(Math.max(0, (Date.now() - startMs) / 1000));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [startedAt]);

  if (session.status !== 'running' || idx < 0) return null;

  const pct = Math.min(elapsed / QUESTION_BUDGET_SECS, 1);
  const overBudget = elapsed > QUESTION_BUDGET_SECS;
  const SIZE = 56; const STROKE = 5; const R = (SIZE - STROKE) / 2;
  const C = 2 * Math.PI * R;
  const offset = C * (1 - pct);

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 px-5 py-3 rounded-2xl border border-white/15 bg-slate-950/85 backdrop-blur-xl shadow-2xl shadow-black/40"
      data-testid="cast-progress-bar"
    >
      {/* Per-question countdown ring */}
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <svg width={SIZE} height={SIZE} className={overBudget ? 'animate-pulse' : ''}>
          <circle
            cx={SIZE / 2} cy={SIZE / 2} r={R}
            stroke="rgba(255,255,255,0.08)" strokeWidth={STROKE} fill="none"
          />
          <circle
            cx={SIZE / 2} cy={SIZE / 2} r={R}
            stroke={overBudget ? '#F43F5E' : 'var(--lumyn-live)'}
            strokeWidth={STROKE}
            strokeLinecap="round"
            fill="none"
            strokeDasharray={C}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            style={{ transition: 'stroke-dashoffset 0.25s linear, stroke 0.25s' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-mono font-bold tabular-nums text-white text-sm">
          {Math.floor(elapsed)}s
        </div>
      </div>

      {/* Q N of M progress strip */}
      <div className="flex flex-col gap-1.5 min-w-[200px]">
        <div className="flex items-baseline gap-2 text-white">
          <span className="font-display font-bold text-2xl tabular-nums leading-none">
            Q{idx + 1}
          </span>
          <span className="text-sm text-slate-300">of {totalQs}</span>
        </div>
        {/* Dot strip — one dot per question, filled up through current */}
        <div className="flex items-center gap-1">
          {Array.from({ length: totalQs }).map((_, i) => (
            <span
              key={`dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i < idx ? 'w-4 bg-[var(--lumyn-live)]/60' :
                i === idx ? 'w-8 bg-[var(--lumyn-live)] shadow-[0_0_8px_var(--lumyn-live)]' :
                'w-3 bg-white/15'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
